import { AnimatePresence, motion } from "framer-motion"
import { Fragment, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"

export type ModalInstance = {
  closeOnOutsideClick: boolean
  exitOnEsc: boolean
  close: () => void
  content: (api: ModalInstance) => ReactNode
}

export type ModalOptions = {
  closeOnOutsideClick?: boolean
  exitOnEsc?: boolean
}

export type ModalManagerApi = {
  show: (content: (api: ModalInstance) => ReactNode, opt?: ModalOptions) => ModalInstance
}

export const ModalManagerContext = createContext<ModalManagerApi>(null!);

export const useModalManager = () => {
  return useContext(ModalManagerContext);
}

export const ModalManagerProvider = (props: { children: React.ReactNode }) => {
  const [instances, setInstances] = useState<ModalInstance[]>([]);

  const show = useCallback((content: (api: ModalInstance) => React.ReactNode, opt?: ModalOptions) => {
    const instance: ModalInstance = {
      closeOnOutsideClick: opt?.closeOnOutsideClick ?? true,
      exitOnEsc: opt?.exitOnEsc ?? true,
      content,
      close: () => {
        setInstances((instances) => instances.filter(i => i !== instance));
      }
    };

    setInstances((instances) => [...instances, instance]);

    return instance;
  }, []);

  // handle escape key at the document level and inform the top
  // modal instance to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const top = instances[instances.length - 1];
        top?.exitOnEsc && top.close();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    } 
  }, [instances]);

  const modalApi: ModalManagerApi = useMemo(() => ({
    show
  }), [show]);

  return (
    <ModalManagerContext.Provider value={modalApi}>
      {props.children}
      <AnimatePresence>
        {instances.map((instance, i) => (
          <Fragment key={i}>
            {createPortal(
              <motion.div
                className="absolute inset-0 flex flex-col items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  zIndex: 1000
                }}
                onClick={(e) => {
                  if (instance.closeOnOutsideClick && e.target === e.currentTarget) {
                    instance.close()
                  }
                }}
              >
                <motion.div
                  initial={{ scale: 0.7, y: "-100%", opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1, transition: { type: "spring", bounce: 0.5 } }}
                  exit={{ scale: 0.7, y: "-100%", opacity: 0 }}
                  autoFocus
                >
                  {instance.content(instance)}
                </motion.div>
              </motion.div>
              ,
              document.body, `modal-${i}`)}
          </Fragment>
        ))}
      </AnimatePresence>
    </ModalManagerContext.Provider>
  )
}
